import styles from './Button.module.css';

import type { FC, ReactNode } from 'react';
import type { ColorVariant } from 'src/theme';

import React, { memo } from 'react';
import styled from 'styled-components';
import { Spinner } from '@phosphor-icons/react';

import { Typography } from '../Typography';
import { calculateSpacing } from '../core/spacing';

type ButtonProps = {
  title?: string;
  iconLeft?: ReactNode;
  icon?: ReactNode;
  variant?: ColorVariant;
  textColor?: ColorVariant;
  borderColor?: ColorVariant;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  /**
   * For a button whose visible title is not enough to tell it from a twin on the same screen —
   * one `Link wallet` per wallet family. Start it with the visible title (`familyActionName`), so
   * speech input that says what it sees still finds the button (WCAG 2.5.3).
   */
  'aria-label'?: string;
  /** For a button that opens a dialog of its own (the account panel behind the address, app#390). */
  'aria-haspopup'?: 'dialog';
  'aria-expanded'?: boolean;
} & (
  | { type?: never; onClick: () => void }
  /**
   * The button of a `<form>`: a tap submits the form, and the form's `onSubmit` acts — the same
   * code that Enter (Go on a phone keyboard) in any of its fields runs (app#366). No `onClick`, or
   * a tap would act twice.
   */
  | { type: 'submit'; onClick?: never }
);

const DEFAULT_VARIANT = 'white';

export const Button: FC<ButtonProps> = memo((props) => {
  const { title, iconLeft, icon, textColor, loading, ...rest } = props;

  return (
    <Container {...rest} hasIcon={!!icon && !title}>
      {loading ? (
        <Spinner size={22} className={styles.loader} />
      ) : (
        <>
          {iconLeft}
          {title ? (
            <Typography variant="heading" fontWeight="medium" textAlign="center" color={textColor}>
              {title}
            </Typography>
          ) : null}
          {icon}
        </>
      )}
    </Container>
  );
});

const Container = styled.button<{
  variant?: ColorVariant;
  borderColor?: ColorVariant;
  hasIcon: boolean;
}>`
  padding: ${calculateSpacing(4)}px;
  border-radius: ${({ theme }) => theme.borderRadius.round}px;
  background-color: ${({ theme, variant }) => theme.colors[variant || DEFAULT_VARIANT]};
  border: 1px solid
    ${({ theme, borderColor, variant }) => theme.colors[borderColor || variant || DEFAULT_VARIANT]};
  cursor: pointer;
  display: flex;
  justify-content: center;
  gap: 8px;
  align-items: center;
  flex-direction: row;
  ${({ hasIcon }) => (hasIcon ? `width: 54px; height: 54px;` : '')}
  &:disabled {
    opacity: 0.6;
  }
`;
